import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
    DataProviderEnum,
    SensorDataRequestCreateDataTypeEnum,
} from "../../../../../../apiClient/generated";
import { useSensorDataApi } from "../../../../../../hooks";

export const useRollingAveragesData = ({
    siteId,
    startDate,
    endDate,
}: {
    siteId?: string;
    startDate: Date;
    endDate: Date;
}) => {
    const apiClient = useSensorDataApi();

    const { data: requestData, isLoading: requestCreateIsLoading } = useQuery({
        queryKey: [
            "sensorDataCreate",
            siteId,
            startDate,
            endDate,
            SensorDataRequestCreateDataTypeEnum.RollingAverages,
            DataProviderEnum.Longpath,
        ],
        queryFn: async () => {
            const response = await apiClient.sensorDataCreate({
                sensorDataRequestCreateRequest: {
                    siteId: siteId,
                    startDate,
                    endDate,
                    dataType:
                        SensorDataRequestCreateDataTypeEnum.RollingAverages,
                    dataProvider: DataProviderEnum.Longpath,
                },
            });
            return response;
        },
        enabled: !!siteId,
        refetchOnWindowFocus: false,
        staleTime: 3600,
    });

    const { data: responseData, isLoading: requestIsLoading } = useQuery({
        queryKey: ["sensorDataRollingAveragesRetrieve", requestData?.requestId],
        queryFn: async () => {
            const response = await apiClient.sensorDataRollingAveragesRetrieve({
                id: requestData?.requestId,
            });
            return response;
        },
        enabled: !!requestData?.requestId,
        staleTime: 3600,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        refetchInterval: (data) => {
            if (
                (data && data.status === "COMPLETED") ||
                (data && data.status === "FAILED")
            ) {
                return false;
            }
            return 1000;
        },
    });

    const isLoading = useMemo(
        () =>
            requestCreateIsLoading ||
            requestIsLoading ||
            (requestData?.requestId &&
                responseData?.status !== "COMPLETED" &&
                responseData?.status !== "FAILED"),
        [responseData, requestIsLoading, requestCreateIsLoading, requestData],
    );

    return {
        isLoading,
        data: responseData?.responseData || [],
    };
};
